/*  src/utils/fetchWithAuth.js
    --------------------------------------------------------------
    One job → send every request to the same-origin /api/* gateway
    -------------------------------------------------------------- */

export async function fetchWithAuth(path, options = {}) {
  // Determine base URL based on environment
  let baseUrl;

  if (typeof window === "undefined") {
    // Node.js/test environment
    baseUrl = "";
  } else {
    // Browser environment
    if (process.env.NODE_ENV === "production") {
      // In production, use the correct backend URL
      baseUrl =
        process.env.REACT_APP_API_URL || "https://www.pabs.co.nz";
    } else {
      // Development mode - use proxy
      baseUrl = "";
    }
  }

  // Normalize path and build full URL
  const isAbsoluteUrl =
    path.startsWith("http://") || path.startsWith("https://");
  const baseEndsWithApi = baseUrl.endsWith("/api");
  const pathStartsWithApi = path.startsWith("api/") || path.startsWith("/api/");
  const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

  let finalPath = path;

  if (isAbsoluteUrl) {
    baseUrl = ""; // Don't prepend base
    finalPath = path;
  } else if (baseEndsWithApi && pathStartsWithApi) {
    // Avoid duplicating "/api/api"
    finalPath = path.replace(/^\/?api\//, "/");
  } else if (!path.startsWith("/")) {
    finalPath = `/${path}`;
  }

  // Ensure /api prefix is always added for non-absolute URLs
  if (!isAbsoluteUrl && !finalPath.startsWith("/api/")) {
    finalPath = `/api${finalPath}`;
  }

  const url = isAbsoluteUrl ? finalPath : `${cleanBase}${finalPath}`;

  // Auth token handling
  const isSharedView =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/share/");

  let token;
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    token = localStorage.getItem("token");
    if (!token) {
      if (isSharedView) {
        console.warn("No token on shared view – skipping login redirect");
        return null;
      }
      console.warn("No token – redirecting to /login");
      window.location.href = "/login";
      return null;
    }
  } else {
    token = "mock-jwt-token-for-testing";
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };

  // Setup fetch options with timeout
  // Use longer timeout for AI-heavy endpoints (5 minutes), standard timeout (2 minutes) for others
  const isLongRunningEndpoint = path.includes('autonomous') ||
    path.includes('competitor') ||
    path.includes('strategy') ||
    path.includes('generate') ||
    path.includes('image');
  const timeoutMs = isLongRunningEndpoint ? 300000 : 120000; // 5 min for AI, 2 min for others
  
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const fetchOpts = {
    credentials: "include",
    signal: controller.signal,
    ...options,
    headers,
  };

  if (fetchOpts.body && typeof fetchOpts.body !== "string") {
    try {
      fetchOpts.body = JSON.stringify(fetchOpts.body);
    } catch (jsonError) {
      console.error("JSON serialization error:", jsonError);
      throw new Error(`Failed to serialize request body: ${jsonError.message}`);
    }
  }

  try {
    console.log(`↗️  ${fetchOpts.method || "GET"} ${url}`);
    const res = await fetch(url, fetchOpts);
    clearTimeout(timer);

    if (res.status === 401) {
      console.warn("401 – token expired, back to /login");
      localStorage.removeItem("token");
      if (!isSharedView) {
        window.location.href = "/login";
      }
      return null;
    }

    if (res.status === 503 && typeof window !== "undefined") {
      let payload = null;
      try {
        payload = await res.clone().json();
      } catch {
        // A proxy may provide a non-JSON 503; it is still a service outage.
      }
      window.dispatchEvent(new CustomEvent("pabs:service-unavailable", {
        detail: { code: payload?.code || "PABS_SERVICE_UNAVAILABLE" },
      }));
    }

    if (!res.ok) {
      const errText = await res.clone().text();
      console.error(`❌ ${res.status} ${url}\n${errText}`);

      // Log more details for debugging
      console.error("Response details:", {
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        url: res.url,
        responsePreview: errText.substring(0, 200),
      });
    }

    return res;
  } catch (error) {
    clearTimeout(timer);
    
    // Provide user-friendly error messages for common issues
    let userFriendlyMessage = error.message;
    
    if (error.name === 'AbortError') {
      const timeoutMinutes = Math.round(timeoutMs / 60000);
      userFriendlyMessage = `The request timed out after ${timeoutMinutes} minutes. This can happen with complex AI operations. Please try again.`;
      console.error(`⏰ Request timeout (${timeoutMinutes}min) for: ${url}`);
    } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      userFriendlyMessage = 'Network connection error. Please check your internet connection and try again.';
    }
    
    console.error("Fetch error:", error);
    console.error("Network details:", {
      name: error.name,
      message: error.message,
      userFriendlyMessage,
      url: url,
      method: fetchOpts.method || "GET",
      timestamp: new Date().toISOString(),
      isLongRunning: isLongRunningEndpoint,
      timeoutMs,
    });
    
    // Create enhanced error with user-friendly message
    const enhancedError = new Error(userFriendlyMessage);
    enhancedError.originalError = error;
    enhancedError.url = url;
    enhancedError.isTimeout = error.name === 'AbortError';
    throw enhancedError;
  }
}

// Export for CommonJS too
if (typeof module !== "undefined" && module.exports) {
  module.exports = { fetchWithAuth };
}
